import React from 'react';

import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListVisitors } from 'src/features/visitor/api/useListVisitors';
import CellVisitorsActions from 'src/features/visitor/components/cell/CellVisitorsActions';
import { Visitor } from 'src/features/visitor/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { booleanBadgeOptions } from 'src/utils/constants';

const columnHelper = createColumnHelper<Visitor>();
const defaultSearchProps = { globalFilterKey: 'search' };
export default function VisitorsTable() {
    const { formatMessage, locale } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'last_visit_date', desc: true }],
    });
    const { querySearchOptions, ...search } = useSearch(defaultSearchProps);
    const { queryFiltersOptions, ...filters } = useFilters();

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessEntitiesPermission = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);

    const { parkingId } = useParking();

    const orderingKeyMap: Record<string, string> = {
        zones: 'zone_names',
    };
    const orderingOptions = {
        ordering: querySortingOptions.ordering
            ?.split(',')
            .map((field) => {
                const desc = field.startsWith('-');
                const key = desc ? field.slice(1) : field;
                return `${desc ? '-' : ''}${orderingKeyMap[key] ?? key}`;
            })
            .join(','),
    };

    const {
        data: visitorsData,
        isError,
        isLoading,
    } = useListVisitors({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...orderingOptions,
    });

    const CellZones = React.useCallback(
        ({ row }: CellContext<Visitor, unknown>) => {
            const values = row.original.zones;
            const filteredTranslations = values.map(
                ({ translations }) => translations.find((translation) => translation.language === locale)?.name,
            );
            return filteredTranslations.join(', ') || '-';
        },
        [locale],
    );

    const columns = [
        columnHelper.accessor('main_identifier', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: (props) => (
                <CellLinkWithIcon
                    toPath={`/${PATH.VISITORS}/visitor/${props.row.original.id}`}
                    linkTitle={props.row.original.main_identifier?.value}
                    hasPermissions={accessVisitors}
                />
            ),
            meta: {
                title: formatMessage(visitorMessages.identifiers),
            },
        }),
        columnHelper.accessor('owner.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const entityName = row.original?.owner?.name;
                const entityId = row.original?.owner?.id;
                const entityType = row.original?.owner?.type;

                return entityId && entityType ? (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ENTITIES}/${entityType}/${entityId}`}
                        linkTitle={entityName}
                        hasPermissions={accessEntitiesPermission}
                    />
                ) : (
                    '-'
                );
            },
            meta: {
                title: formatMessage(visitorMessages.owner),
            },
        }),
        columnHelper.accessor('in_parking', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(visitorMessages.inParking),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.type),
                badgeOptions: visitorTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('attributes', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const values = Object.values(row.original.attributes);
                return values.length ? values.join(' - ') : '-';
            },
            meta: {
                title: formatMessage(visitorMessages.attributes),
            },
        }),
        columnHelper.accessor('zones', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellZones,
            meta: {
                title: formatMessage(visitorMessages.visitedZones),
            },
        }),
        columnHelper.accessor('last_visit_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(visitorMessages.lastVisit),
            },
        }),
        columnHelper.accessor('total_visits_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.visits),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellVisitorsActions,
        }),
    ];

    return (
        <DataTable<Visitor, any>
            columns={columns}
            data={visitorsData}
            pagination={pagination}
            sorting={sorting}
            search={search}
            filters={filters}
            semantics={{
                title: formatMessage(visitorMessages.visitors),
                entryName: formatMessage(visitorMessages.visitor).toLowerCase(),
                entryNames: formatMessage(visitorMessages.visitors).toLowerCase(),
                placeholder: formatMessage(visitorMessages.ownerOrIdentifier),
            }}
            isError={isError}
            isLoading={isLoading}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
            enableLocalFilters={false}
        />
    );
}
